import {MathUtils, Vector2} from 'three'

const SAMPLE_DISTANCE = 0.025
const MAX_WAYPOINTS = 30
const END_DISTANCE = 0.002
const BASE_SPEED = 1
const MAX_CATCH_UP_SPEED = 3
const STOPPING_DISTANCE = 0.2
const MIN_SPEED_SCALE = 0.25

export default class PointerTrail {
  readonly pointerPosition = new Vector2()
  readonly position = new Vector2()
  readonly #lastSample = new Vector2()
  readonly #waypoints: Vector2[] = []
  remainingDistance = 0
  hasSample = false

  clear() {
    this.#waypoints.length = 0
    this.remainingDistance = 0
    this.hasSample = false
  }

  sample(pointerPosition: Vector2) {
    if (!this.hasSample) {
      this.#lastSample.copy(pointerPosition)
      this.position.copy(pointerPosition)
      this.hasSample = true
    } else if (this.#lastSample.distanceTo(pointerPosition) >= SAMPLE_DISTANCE) {
      this.#waypoints.push(pointerPosition.clone())
      this.#lastSample.copy(pointerPosition)

      if (this.#waypoints.length > MAX_WAYPOINTS) {
        this.#waypoints.shift()
      }
    }

    this.pointerPosition.copy(pointerPosition)
  }

  update(delta: number) {
    if (!this.hasSample) return

    const remainingDistanceBeforeUpdate = this.#getRemainingDistance()
    const stoppingSpeedProgress = MathUtils.smoothstep(remainingDistanceBeforeUpdate, 0, STOPPING_DISTANCE)
    const speedScale = MathUtils.lerp(MIN_SPEED_SCALE, 1, stoppingSpeedProgress)
    const catchUpSpeed = Math.min(this.#waypoints.length * 0.1, MAX_CATCH_UP_SPEED)
    let remainingMovement = (BASE_SPEED + catchUpSpeed) * speedScale * delta

    while (remainingMovement > 0) {
      const target = this.#waypoints[0] ?? this.pointerPosition
      const distance = this.position.distanceTo(target)

      if (distance <= END_DISTANCE) {
        this.position.copy(target)
        if (this.#waypoints.length === 0) break
        this.#waypoints.shift()
        continue
      }

      if (distance <= remainingMovement) {
        this.position.copy(target)
        remainingMovement -= distance
        if (this.#waypoints.length === 0) break
        this.#waypoints.shift()
        continue
      }

      this.position.lerp(target, remainingMovement / distance)
      break
    }

    this.remainingDistance = this.#getRemainingDistance()
  }

  isSettled(maxRemainingDistance: number) {
    return this.#waypoints.length === 0 && this.remainingDistance <= maxRemainingDistance
  }

  #getRemainingDistance() {
    let remainingDistance = 0
    let previousPoint = this.position

    for (const point of this.#waypoints) {
      remainingDistance += previousPoint.distanceTo(point)
      previousPoint = point
    }

    return remainingDistance + previousPoint.distanceTo(this.pointerPosition)
  }
}
